import { CSElementPlugin } from '../types/plugin-interfaces';
import { CSElement } from '../core/CSElement';

// Уровни безопасности
export enum SecurityLevel {
    PUBLIC = 'public',
    PROTECTED = 'protected',
    PRIVATE = 'private',
    CONFIDENTIAL = 'confidential',
    TOP_SECRET = 'top_secret'
}

// Типы разрешений
export enum PermissionType {
    READ = 'read',
    WRITE = 'write',
    DELETE = 'delete',
    EXECUTE = 'execute',
    ADMIN = 'admin'
}

// Методы аутентификации
export enum AuthenticationMethod {
    PASSWORD = 'password',
    TOKEN = 'token',
    CERTIFICATE = 'certificate',
    BIOMETRIC = 'biometric',
    MULTI_FACTOR = 'multi_factor'
}

// Алгоритмы шифрования
export enum EncryptionAlgorithm {
    AES_256 = 'aes-256',
    RSA_2048 = 'rsa-2048',
    ECDSA = 'ecdsa',
    CHACHA20 = 'chacha20'
}

// Правила безопасности
export interface SecurityRule {
    id: string;
    name: string;
    description?: string;
    level: SecurityLevel;
    permissions: PermissionType[];
    conditions?: SecurityCondition[];
    actions?: SecurityAction[];
    enabled: boolean;
    priority: number;
    createdAt: Date;
    updatedAt: Date;
}

export interface SecurityCondition {
    type: 'user' | 'role' | 'time' | 'location' | 'custom';
    operator: 'equals' | 'not_equals' | 'contains' | 'matches' | 'greater' | 'less';
    value: any;
    field?: string;
}

export interface SecurityAction {
    type: 'allow' | 'deny' | 'log' | 'alert' | 'encrypt' | 'audit';
    parameters?: Record<string, any>;
}

// Пользователь
export interface User {
    id: string;
    username: string;
    email?: string;
    roles: string[];
    permissions: PermissionType[];
    authMethods: AuthenticationMethod[];
    securityLevel: SecurityLevel;
    isActive: boolean;
    lastLogin?: Date;
    failedAttempts: number;
    lockedUntil?: Date;
    metadata?: Record<string, any>;
}

// Роль
export interface Role {
    id: string;
    name: string;
    description?: string;
    permissions: PermissionType[];
    securityLevel: SecurityLevel;
    inherits?: string[];
    isActive: boolean;
}

// Контекст безопасности
export interface SecurityContext {
    user?: User;
    sessionId?: string;
    ipAddress?: string;
    userAgent?: string;
    timestamp: Date;
    operation: string;
    resource?: string;
    metadata?: Record<string, any>;
}

// Конфигурация шифрования
export interface EncryptionConfig {
    algorithm: EncryptionAlgorithm;
    keySize: number;
    iv?: string;
    salt?: string;
    iterations?: number;
}

// Аудит лог
export interface AuditLog {
    id: string;
    timestamp: Date;
    user?: string;
    operation: string;
    resource?: string;
    result: 'success' | 'failure' | 'denied';
    details?: Record<string, any>;
    securityLevel: SecurityLevel;
    ipAddress?: string;
    userAgent?: string;
}

// Конфигурация плагина
export interface SecurityPluginConfig {
    enabled: boolean;
    defaultSecurityLevel: SecurityLevel;
    encryptionConfig: EncryptionConfig;
    sessionTimeout: number;
    maxFailedAttempts: number;
    lockoutDuration: number;
    auditingEnabled: boolean;
    encryptionEnabled: boolean;
    requireAuthentication: boolean;
    allowAnonymous: boolean;
    passwordPolicy?: {
        minLength: number;
        requireUppercase: boolean;
        requireLowercase: boolean;
        requireNumbers: boolean;
        requireSymbols: boolean;
        maxAge: number;
    };
}

// Статистика безопасности
export interface SecurityStats {
    totalUsers: number;
    activeUsers: number;
    lockedUsers: number;
    totalRoles: number;
    totalRules: number;
    activeRules: number;
    auditLogs: number;
    failedAttempts: number;
    successfulLogins: number;
    deniedOperations: number;
    encryptedElements: number;
    lastAuditCleanup?: Date;
}

export class SecurityPlugin implements CSElementPlugin {
    public readonly name = 'SecurityPlugin';
    public readonly version = '1.0.0';
    public readonly description = 'Плагин для обеспечения безопасности, аутентификации и авторизации';

    private config: SecurityPluginConfig;
    private users = new Map<string, User>();
    private roles = new Map<string, Role>();
    private rules = new Map<string, SecurityRule>();
    private sessions = new Map<string, SecurityContext>();
    private auditLogs: AuditLog[] = [];
    private _encryptionKeys = new Map<string, string>(); // Для хранения ключей шифрования

    /**
     * Установка ключа шифрования для элемента
     */
    setEncryptionKey(elementId: string, key: string): void {
        this._encryptionKeys.set(elementId, key);
    }

    /**
     * Получение ключа шифрования для элемента
     */
    getEncryptionKey(elementId: string): string | undefined {
        return this._encryptionKeys.get(elementId);
    }
    private stats: SecurityStats;

    constructor(config: Partial<SecurityPluginConfig> = {}) {
        this.config = {
            enabled: true,
            defaultSecurityLevel: SecurityLevel.PROTECTED,
            encryptionConfig: {
                algorithm: EncryptionAlgorithm.AES_256,
                keySize: 256,
                iterations: 10000
            },
            sessionTimeout: 3600000, // 1 час
            maxFailedAttempts: 5,
            lockoutDuration: 900000, // 15 минут
            auditingEnabled: true,
            encryptionEnabled: true,
            requireAuthentication: false,
            allowAnonymous: true,
            ...config
        };

        this.stats = {
            totalUsers: 0,
            activeUsers: 0,
            lockedUsers: 0,
            totalRoles: 0,
            totalRules: 0,
            activeRules: 0,
            auditLogs: 0,
            failedAttempts: 0,
            successfulLogins: 0,
            deniedOperations: 0,
            encryptedElements: 0
        };

        this.initializeDefaultRoles();
    }

    install(): void {
        console.log(`Установка ${this.name} v${this.version}`);
        
        // Очистка истекших сессий
        setInterval(() => this.cleanupExpiredSessions(), 60000); // каждую минуту
        
        // Очистка старых аудит логов
        if (this.config.auditingEnabled) {
            setInterval(() => this.cleanupAuditLogs(), 3600000); // каждый час
        }
    }

    uninstall(): void {
        this.sessions.clear();
        console.log(`${this.name} деинициализирован`);
    }

    getConfig(): SecurityPluginConfig {
        return { ...this.config };
    }

    updateConfig(newConfig: Partial<SecurityPluginConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    // Lifecycle hooks
    async beforeCreate(element: CSElement, context: any): Promise<any> {
        if (!this.config.enabled) return { success: true };

        const securityContext = this.getSecurityContext(context);
        const hasPermission = await this.checkPermission(securityContext, PermissionType.WRITE);
        
        if (!hasPermission) {
            this.logAudit(securityContext, 'create_element', 'denied', { elementId: element.id });
            return { 
                success: false, 
                error: 'Недостаточно прав для создания элемента',
                metadata: { securityLevel: this.config.defaultSecurityLevel }
            };
        }

        // Установка уровня безопасности
        if (!element.getData('securityLevel')) {
            element.setData('securityLevel', this.config.defaultSecurityLevel);
        }

        this.logAudit(securityContext, 'create_element', 'success', { elementId: element.id });
        return { success: true };
    }

    async beforeUpdate(element: CSElement, context: any): Promise<any> {
        if (!this.config.enabled) return { success: true };

        const securityContext = this.getSecurityContext(context);
        const elementSecurityLevel = element.getData('securityLevel') || this.config.defaultSecurityLevel;
        
        const hasPermission = await this.checkElementAccess(securityContext, element, PermissionType.WRITE);
        
        if (!hasPermission) {
            this.logAudit(securityContext, 'update_element', 'denied', { 
                elementId: element.id,
                securityLevel: elementSecurityLevel
            });
            return { 
                success: false, 
                error: 'Недостаточно прав для изменения элемента' 
            };
        }

        this.logAudit(securityContext, 'update_element', 'success', { elementId: element.id });
        return { success: true };
    }

    async beforeDelete(element: CSElement, context: any): Promise<any> {
        if (!this.config.enabled) return { success: true };

        const securityContext = this.getSecurityContext(context);
        const hasPermission = await this.checkElementAccess(securityContext, element, PermissionType.DELETE);
        
        if (!hasPermission) {
            this.logAudit(securityContext, 'delete_element', 'denied', { elementId: element.id });
            return { 
                success: false, 
                error: 'Недостаточно прав для удаления элемента' 
            };
        }

        this.logAudit(securityContext, 'delete_element', 'success', { elementId: element.id });
        return { success: true };
    }

    async afterRead(element: CSElement, context: any): Promise<any> {
        if (!this.config.enabled) return { success: true };

        const securityContext = this.getSecurityContext(context);
        const hasPermission = await this.checkElementAccess(securityContext, element, PermissionType.READ);
        
        if (!hasPermission) {
            this.logAudit(securityContext, 'read_element', 'denied', { elementId: element.id });
            return { 
                success: false, 
                error: 'Недостаточно прав для чтения элемента' 
            };
        }

        // Расшифровка данных если необходимо
        if (this.config.encryptionEnabled) {
            await this.decryptElementData(element, securityContext);
        }

        this.logAudit(securityContext, 'read_element', 'success', { elementId: element.id });
        return { success: true };
    }

    // Управление пользователями
    createUser(userData: Omit<User, 'id' | 'failedAttempts' | 'isActive'>): User {
        const user: User = {
            id: this.generateId(),
            failedAttempts: 0,
            isActive: true,
            ...userData
        };
        
        this.users.set(user.id, user);
        this.updateStats();
        return user;
    }

    getUser(id: string): User | undefined {
        return this.users.get(id);
    }

    getUserByUsername(username: string): User | undefined {
        for (const user of this.users.values()) {
            if (user.username === username) {
                return user;
            }
        }
        return undefined;
    }

    updateUser(id: string, updates: Partial<User>): boolean {
        const user = this.users.get(id);
        if (!user) return false;
        
        Object.assign(user, updates);
        this.updateStats();
        return true;
    }

    deleteUser(id: string): boolean {
        const deleted = this.users.delete(id);
        if (deleted) this.updateStats();
        return deleted;
    }

    // Управление ролями
    createRole(roleData: Omit<Role, 'id'>): Role {
        const role: Role = {
            id: this.generateId(),
            ...roleData
        };
        
        this.roles.set(role.id, role);
        this.updateStats();
        return role;
    }

    getRole(id: string): Role | undefined {
        return this.roles.get(id);
    }

    updateRole(id: string, updates: Partial<Role>): boolean {
        const role = this.roles.get(id);
        if (!role) return false;
        
        Object.assign(role, updates);
        this.updateStats();
        return true;
    }

    deleteRole(id: string): boolean {
        const deleted = this.roles.delete(id);
        if (deleted) this.updateStats();
        return deleted;
    }

    // Управление правилами безопасности
    addSecurityRule(ruleData: Omit<SecurityRule, 'id' | 'createdAt' | 'updatedAt'>): SecurityRule {
        const rule: SecurityRule = {
            id: this.generateId(),
            createdAt: new Date(),
            updatedAt: new Date(),
            ...ruleData
        };
        
        this.rules.set(rule.id, rule);
        this.updateStats();
        return rule;
    }

    getSecurityRule(id: string): SecurityRule | undefined {
        return this.rules.get(id);
    }

    updateSecurityRule(id: string, updates: Partial<SecurityRule>): boolean {
        const rule = this.rules.get(id);
        if (!rule) return false;
        
        Object.assign(rule, { ...updates, updatedAt: new Date() });
        this.updateStats();
        return true;
    }

    deleteSecurityRule(id: string): boolean {
        const deleted = this.rules.delete(id);
        if (deleted) this.updateStats();
        return deleted;
    }

    // Аутентификация и авторизация
    async authenticate(username: string, password: string, _method: AuthenticationMethod = AuthenticationMethod.PASSWORD): Promise<string | null> {
        const user = this.getUserByUsername(username);
        if (!user || !user.isActive) {
            this.stats.failedAttempts++;
            return null;
        }

        // Проверка блокировки
        if (user.lockedUntil && user.lockedUntil > new Date()) {
            return null;
        }

        // Проверка пароля
        const isValidPassword = await this.verifyPassword(password, user);
        if (!isValidPassword) {
            user.failedAttempts++;
            this.stats.failedAttempts++;
            
            // Блокировка при превышении попыток
            if (user.failedAttempts >= this.config.maxFailedAttempts) {
                user.lockedUntil = new Date(Date.now() + this.config.lockoutDuration);
                this.updateStats();
            }
            
            return null;
        }

        // Успешная аутентификация
        user.failedAttempts = 0;
        user.lastLogin = new Date();
        user.lockedUntil = undefined;
        
        const sessionId = this.generateSessionId();
        const session: SecurityContext = {
            user,
            sessionId,
            timestamp: new Date(),
            operation: 'login'
        };
        
        this.sessions.set(sessionId, session);
        this.stats.successfulLogins++;
        this.updateStats();
        
        return sessionId;
    }

    logout(sessionId: string): boolean {
        return this.sessions.delete(sessionId);
    }

    getSession(sessionId: string): SecurityContext | undefined {
        const session = this.sessions.get(sessionId);
        if (!session) return undefined;
        
        // Проверка истечения сессии
        if (Date.now() - session.timestamp.getTime() > this.config.sessionTimeout) {
            this.sessions.delete(sessionId);
            return undefined;
        }
        
        return session;
    }

    async checkPermission(context: SecurityContext, permission: PermissionType): Promise<boolean> {
        if (!this.config.requireAuthentication && this.config.allowAnonymous) {
            return true;
        }

        if (!context.user) {
            return false;
        }

        const user = context.user;
        
        // Проверка прямых разрешений пользователя
        if (user.permissions.includes(permission) || user.permissions.includes(PermissionType.ADMIN)) {
            return true;
        }

        // Проверка разрешений через роли
        for (const roleId of user.roles) {
            const role = this.roles.get(roleId);
            if (role && role.isActive && (role.permissions.includes(permission) || role.permissions.includes(PermissionType.ADMIN))) {
                return true;
            }
        }

        return false;
    }

    async checkElementAccess(context: SecurityContext, element: CSElement, permission: PermissionType): Promise<boolean> {
        const hasBasePermission = await this.checkPermission(context, permission);
        if (!hasBasePermission) {
            return false;
        }

        const elementSecurityLevel = element.getData('securityLevel') || this.config.defaultSecurityLevel;
        const userSecurityLevel = context.user?.securityLevel || SecurityLevel.PUBLIC;

        return this.compareSecurityLevels(userSecurityLevel, elementSecurityLevel);
    }

    async encryptElementData(element: CSElement, _context: SecurityContext): Promise<boolean> {
        try {
            const data = element.serialize();
            const encryptedData = await this.encrypt(JSON.stringify(data));
            element.setData('_encrypted', true);
            element.setData('_encryptedData', encryptedData);
            this.stats.encryptedElements++;
            return true;
        } catch (error) {
            console.error('Ошибка шифрования данных элемента:', error);
            return false;
        }
    }

    async decryptElementData(element: CSElement, _context: SecurityContext): Promise<boolean> {
        try {
            if (!element.getData('_encrypted')) {
                return true; // Данные не зашифрованы
            }

            const encryptedData = element.getData('_encryptedData');
            if (!encryptedData) {
                return false;
            }

            const decryptedData = await this.decrypt(encryptedData);
            const data = JSON.parse(decryptedData);
            
            // Восстановление данных
            Object.keys(data).forEach(key => {
                if (key !== '_encrypted' && key !== '_encryptedData') {
                    element.setData(key, data[key]);
                }
            });

            element.deleteData('_encrypted');
            element.deleteData('_encryptedData');
            return true;
        } catch (error) {
            console.error('Ошибка расшифровки данных элемента:', error);
            return false;
        }
    }

    // Аудит
    private logAudit(context: SecurityContext, operation: string, result: 'success' | 'failure' | 'denied', details?: Record<string, any>): void {
        if (!this.config.auditingEnabled) return;

        const auditLog: AuditLog = {
            id: this.generateId(),
            timestamp: new Date(),
            user: context.user?.username,
            operation,
            resource: context.resource,
            result,
            details,
            securityLevel: context.user?.securityLevel || SecurityLevel.PUBLIC,
            ipAddress: context.ipAddress,
            userAgent: context.userAgent
        };

        this.auditLogs.push(auditLog);
        this.stats.auditLogs++;
        
        if (result === 'denied') {
            this.stats.deniedOperations++;
        }
    }

    getAuditLogs(filter?: Partial<AuditLog>): AuditLog[] {
        if (!filter) return [...this.auditLogs];

        return this.auditLogs.filter(log => {
            return Object.entries(filter).every(([key, value]) => {
                return (log as any)[key] === value;
            });
        });
    }

    private getSecurityContext(context: any): SecurityContext {
        return {
            user: context?.user,
            sessionId: context?.sessionId,
            ipAddress: context?.ipAddress,
            userAgent: context?.userAgent,
            timestamp: new Date(),
            operation: context?.operation || 'unknown',
            resource: context?.resource,
            metadata: context?.metadata
        };
    }

    private compareSecurityLevels(userLevel: SecurityLevel, requiredLevel: SecurityLevel): boolean {
        const levels = [
            SecurityLevel.PUBLIC,
            SecurityLevel.PROTECTED,
            SecurityLevel.PRIVATE,
            SecurityLevel.CONFIDENTIAL,
            SecurityLevel.TOP_SECRET
        ];

        const userLevelIndex = levels.indexOf(userLevel);
        const requiredLevelIndex = levels.indexOf(requiredLevel);

        return userLevelIndex >= requiredLevelIndex;
    }

    private async verifyPassword(password: string, _user: User): Promise<boolean> {
        // Простая проверка пароля (в реальном приложении должен быть хеш)
        return password.length >= 8; // Минимальная проверка
    }

    private async encrypt(data: string): Promise<string> {
        // Простое шифрование для демонстрации
        return Buffer.from(data).toString('base64');
    }

    private async decrypt(encryptedData: string): Promise<string> {
        // Простая расшифровка для демонстрации
        return Buffer.from(encryptedData, 'base64').toString();
    }

    private initializeDefaultRoles(): void {
        // Создание базовых ролей
        this.createRole({
            name: 'admin',
            description: 'Администратор системы',
            permissions: [PermissionType.ADMIN],
            securityLevel: SecurityLevel.TOP_SECRET,
            isActive: true
        });

        this.createRole({
            name: 'user',
            description: 'Обычный пользователь',
            permissions: [PermissionType.READ, PermissionType.WRITE],
            securityLevel: SecurityLevel.PROTECTED,
            isActive: true
        });

        this.createRole({
            name: 'guest',
            description: 'Гостевой доступ',
            permissions: [PermissionType.READ],
            securityLevel: SecurityLevel.PUBLIC,
            isActive: true
        });
    }

    private cleanupExpiredSessions(): void {
        const now = Date.now();
        for (const [sessionId, session] of this.sessions.entries()) {
            if (now - session.timestamp.getTime() > this.config.sessionTimeout) {
                this.sessions.delete(sessionId);
            }
        }
        this.updateStats();
    }

    private cleanupAuditLogs(): void {
        // Удаление логов старше 30 дней
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        this.auditLogs = this.auditLogs.filter(log => log.timestamp.getTime() > thirtyDaysAgo);
        this.stats.auditLogs = this.auditLogs.length;
        this.stats.lastAuditCleanup = new Date();
    }

    private updateStats(): void {
        this.stats.totalUsers = this.users.size;
        this.stats.activeUsers = Array.from(this.users.values()).filter(u => u.isActive).length;
        this.stats.lockedUsers = Array.from(this.users.values()).filter(u => u.lockedUntil && u.lockedUntil > new Date()).length;
        this.stats.totalRoles = this.roles.size;
        this.stats.totalRules = this.rules.size;
        this.stats.activeRules = Array.from(this.rules.values()).filter(r => r.enabled).length;
    }

    private generateId(): string {
        return Math.random().toString(36).substr(2, 9);
    }

    private generateSessionId(): string {
        return Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
    }

    // Публичные методы для получения статистики и данных
    getStats(): SecurityStats {
        return { ...this.stats };
    }

    getAllUsers(): User[] {
        return Array.from(this.users.values());
    }

    getAllRoles(): Role[] {
        return Array.from(this.roles.values());
    }

    getAllSecurityRules(): SecurityRule[] {
        return Array.from(this.rules.values());
    }

    getActiveSessions(): SecurityContext[] {
        return Array.from(this.sessions.values());
    }
} 